/**
 * R2 / CDN URL builder.
 *
 * Handles Trenchmate-specific CDN URL resolution:
 *  - NEXT_PUBLIC_R2_URL as the public base
 *  - Absolute URL remapping (old pub-*.r2.dev → configured CDN)
 *  - Path encoding for safe URLs
 *
 * For simpler projects, @techstream/quark-core/storage provides getAssetUrl()
 * which reads ASSET_CDN_URL. This module retains Trenchmate's additional
 * logic (absolute URL remapping, globalThis fallback, query-string encoding).
 */

// Small helper to ensure we return a string without a trailing slash.
function trimTrailingSlash(s) {
    try {
        return String(s).replace(/\/$/, '');
    } catch (_e) {
        return '';
    }
}

function encodeKey(s3Key) {
    if (!s3Key) return '';
    return s3Key.split('/').map(encodeURIComponent).join('/');
}

function isAbsoluteUrl(val) {
    return typeof val === 'string' && /^https?:\/\//i.test(val);
}

function buildPublicUrl(s3Key) {
    if (!s3Key) return '';

    let key = s3Key;
    if (isAbsoluteUrl(s3Key)) {
        try {
            const parsed = new URL(s3Key);
            key = (parsed.pathname + (parsed.search || '')).replace(/^\/+/, '');
        } catch (_e) {
            key = s3Key.replace(/^\/+/, '');
        }
    } else {
        key = (s3Key || '').replace(/^\/+/, '');
    }

    const encoded = encodeKey(key);
    const publicBase = resolvePublicBase();
    if (publicBase) return `${publicBase}/${encoded}`;
    return `/${encoded}`;
}

/**
 * Resolve the public base URL.
 * Priority: NEXT_PUBLIC_R2_URL (client-safe) → globalThis fallback → empty.
 */
function resolvePublicBase() {
    try {
        if (
            typeof process !== 'undefined' &&
            process.env &&
            process.env.NEXT_PUBLIC_R2_URL
        ) {
            return trimTrailingSlash(process.env.NEXT_PUBLIC_R2_URL);
        }
    } catch (_e) {
        // ignore
    }

    if (
        typeof globalThis !== 'undefined' &&
        globalThis.__NEXT_PUBLIC_R2_URL__
    ) {
        try {
            return trimTrailingSlash(String(globalThis.__NEXT_PUBLIC_R2_URL__));
        } catch (_e) {
            // ignore
        }
    }

    return '';
}

/**
 * Check if a URL points to the Trenchmate CDN.
 *
 * @param {string} url - The URL to check
 * @returns {boolean} - True if the URL is on the Trenchmate CDN
 */
function isCdnUrl(url) {
    if (!url) return false;
    return /cdn\.trenchmate\.techstream\.co\.nz/i.test(url);
}

// Export as CommonJS for the runtime and tests that expect require/module.exports
module.exports = { buildPublicUrl, encodeKey, isCdnUrl };

// Also support ESM import { buildPublicUrl } from '.../r2-urls.js' by defining
// an __esModule flag and default export. This helps bundlers and ESM loaders
// that perform interop with CommonJS modules.
try {
    Object.defineProperty(module.exports, '__esModule', { value: true });
    module.exports.default = buildPublicUrl;
} catch (_e) {
    // ignore
}
